import React, { useEffect, useState } from 'react';
import { C, S } from '../../utils/styles';
import Icon from '../Icon';
import { useToast } from '../Toast';
import { getMyCertificate, publicVerifyUrl, qrImageUrl } from './learnerApi';

const HC_LOGO = 'https://customer-assets.emergentagent.com/job_crazy-ellis-3/artifacts/hydsmihy_HealthCues_Workplace_Health_Solutions_Logo_simple.svg';

export default function CertificateView({ certificateId, onBack }) {
  const showToast = useToast();
  const [cert, setCert] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let mounted = true;
    getMyCertificate(certificateId)
      .then((d) => { if (mounted) setCert(d); })
      .catch((err) => showToast(err.message || 'Failed to load certificate', 'error'))
      .finally(() => { if (mounted) setLoading(false); });
    return () => { mounted = false; };
  }, [certificateId, showToast]);

  const handlePrint = () => window.print();

  if (loading) {
    return (
      <div style={{ maxWidth: 900, margin: '0 auto', padding: 24 }} data-testid="certificate-loading">
        <div style={S.card}>Loading certificate…</div>
      </div>
    );
  }
  if (!cert) {
    return <div style={{ padding: 24, color: C.textMid }}>Certificate not found.</div>;
  }

  return (
    <div data-testid="certificate-view" style={{ maxWidth: 980, margin: '0 auto', padding: '24px 12px' }}>
      {/* Action bar (hidden in print) */}
      <div className="no-print" style={{ display: 'flex', gap: 8, alignItems: 'center', marginBottom: 16, flexWrap: 'wrap' }}>
        <button type="button" data-testid="cert-back" onClick={onBack} style={S.buttonSecondary}>
          <Icon name="arrow-back-outline" style={{ fontSize: 14, marginRight: 4, verticalAlign: '-1px' }} />
          My Training
        </button>
        <div style={{ flex: 1 }} />
        <button type="button" data-testid="cert-print" onClick={handlePrint} style={S.buttonPrimary}>
          <Icon name="download-outline" style={{ fontSize: 14, marginRight: 6, verticalAlign: '-1px' }} />
          Download / Print
        </button>
      </div>

      <CertificateDocument cert={cert} />
    </div>
  );
}

export function CertificateDocument({ cert, verified = true }) {
  return (
    <>
      <style>{`
        @media print {
          .no-print { display: none !important; }
          body { background: white !important; }
          .hc-cert { box-shadow: none !important; border: none !important; page-break-inside: avoid; }
        }
      `}</style>
      <div
        className="hc-cert"
        data-testid="certificate-doc"
        style={{
          background: 'linear-gradient(135deg, #0e7490 0%, #06b6d4 50%, #22d3ee 100%)',
          padding: 6,
          borderRadius: 18,
          boxShadow: '0 12px 36px rgba(15, 23, 42, 0.18)',
        }}
      >
        <div
          style={{
            background: '#ffffff',
            borderRadius: 14,
            padding: '48px 56px',
            position: 'relative',
            overflow: 'hidden',
          }}
        >
          {/* Decorative corner flourishes */}
          <div style={{ position: 'absolute', top: -40, right: -40, width: 200, height: 200, borderRadius: '50%', background: 'linear-gradient(135deg, #ecfeff, transparent)', opacity: 0.6 }} />
          <div style={{ position: 'absolute', bottom: -40, left: -40, width: 240, height: 240, borderRadius: '50%', background: 'linear-gradient(135deg, #cffafe, transparent)', opacity: 0.5 }} />

          {/* Header */}
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', position: 'relative', zIndex: 1 }}>
            <img src={HC_LOGO} alt="HealthCues" style={{ height: 48 }} />
            <div style={{ textAlign: 'right' }}>
              <div style={{ fontSize: 11, color: C.textLight, fontWeight: 800, letterSpacing: '0.12em', textTransform: 'uppercase' }}>Certificate of Completion</div>
              <div style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: 11, color: C.accent, fontWeight: 700, marginTop: 2 }}>{cert.id}</div>
            </div>
          </div>

          {/* Body */}
          <div style={{ textAlign: 'center', marginTop: 36, position: 'relative', zIndex: 1 }}>
            <div style={{ fontSize: 13, color: C.textMid, fontWeight: 600, letterSpacing: '0.04em' }}>
              This certifies that
            </div>
            <div style={{ fontSize: 40, fontWeight: 800, color: C.text, marginTop: 8, letterSpacing: '-0.02em', lineHeight: 1.1 }}>
              {cert.userName || 'HealthCues Learner'}
            </div>
            <div style={{ width: 120, height: 2, background: C.accent, margin: '14px auto 18px' }} />
            <div style={{ fontSize: 13, color: C.textMid, fontWeight: 600, letterSpacing: '0.04em' }}>
              has successfully completed
            </div>
            <div style={{ fontSize: 26, fontWeight: 700, color: C.accent, marginTop: 6, letterSpacing: '-0.01em' }}>
              {cert.courseTitle}
            </div>
            <div style={{ fontSize: 12, color: C.textLight, marginTop: 6 }}>
              Course version {cert.courseVersion} · Score {cert.scorePercent}% (passing {cert.passPercent}%)
            </div>
          </div>

          {/* Footer / signature row */}
          <div style={{ marginTop: 48, display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end', gap: 24, position: 'relative', zIndex: 1, flexWrap: 'wrap' }}>
            <div style={{ minWidth: 200 }}>
              <div style={{ borderBottom: `2px solid ${C.text}`, paddingBottom: 4, fontFamily: "'Playfair Display', serif", fontStyle: 'italic', fontSize: 22, color: C.text }}>
                HealthCues Learning
              </div>
              <div style={{ fontSize: 10, color: C.textLight, fontWeight: 700, letterSpacing: '0.08em', textTransform: 'uppercase', marginTop: 6 }}>
                Authorized signature
              </div>
            </div>
            <div style={{ textAlign: 'center' }}>
              {cert.id && (
                <img
                  data-testid="cert-qr"
                  src={qrImageUrl(cert.id, 140)}
                  alt="Verification QR"
                  style={{ width: 110, height: 110, display: 'block', border: `1px solid ${C.borderLight}`, padding: 4, borderRadius: 8, background: '#fff' }}
                />
              )}
              <div style={{ fontSize: 9, color: C.textLight, marginTop: 6, maxWidth: 130, lineHeight: 1.4 }}>
                Scan to verify at<br />
                <span style={{ color: C.accent, fontWeight: 600, wordBreak: 'break-all' }}>{publicVerifyUrl(cert.id)}</span>
              </div>
            </div>
            <div style={{ minWidth: 200, textAlign: 'right' }}>
              <div style={{ borderBottom: `2px solid ${C.text}`, paddingBottom: 4, fontFamily: "'JetBrains Mono', monospace", fontSize: 14, color: C.text }}>
                {cert.issuedAt ? new Date(cert.issuedAt).toLocaleDateString(undefined, { year: 'numeric', month: 'long', day: 'numeric' }) : ''}
              </div>
              <div style={{ fontSize: 10, color: C.textLight, fontWeight: 700, letterSpacing: '0.08em', textTransform: 'uppercase', marginTop: 6 }}>
                Date issued
              </div>
            </div>
          </div>

          {verified && (
            <div style={{ position: 'absolute', top: 20, left: 20, fontSize: 10, color: C.green, fontWeight: 800, letterSpacing: '0.08em', textTransform: 'uppercase', background: C.greenLight, padding: '4px 10px', borderRadius: 999, border: `1px solid ${C.greenBorder}` }}>
              <Icon name="shield-checkmark-outline" style={{ fontSize: 11, marginRight: 4, verticalAlign: '-1px' }} />
              Verified
            </div>
          )}
        </div>
      </div>
    </>
  );
}
